import { css } from '@emotion/css'
import { borderRadius, spacing, fontSize, fontFamily, fontWeight, lineHeight } from '../../styles'
import { shorthandHelper } from '../../styles/helpers'
import getThemeColors, { colors } from '../../themes'

const styles = {
  preview: css({
    wordBreak: 'break-word',
    color: colors.jumbo,

    '& h1': {
      margin: 0,
      fontSize: fontSize.medium,
      fontWeight: fontWeight.semiBold,
      lineHeight: 1.2,
      color: colors.oxfordBlue,
      fontFamily: fontFamily.body,
    },

    '& h2': {
      margin: 0,
      fontSize: fontSize.medium,
      fontWeight: fontWeight.regular,
      lineHeight: 1.2,
      color: colors.oxfordBlue,
      fontFamily: fontFamily.body,
    },

    '& h3': {
      margin: 0,
      fontSize: fontSize.default,
      fontWeight: fontWeight.semiBold,
      lineHeight: 1.4,
      color: colors.oxfordBlue,
      fontFamily: fontFamily.body,
    },

    '& h4': {
      margin: 0,
      fontSize: fontSize.default,
      fontWeight: fontWeight.regular,
      lineHeight: 1.4,
      color: colors.oxfordBlue,
      fontFamily: fontFamily.body,
    },

    '& h5': {
      margin: 0,
      fontSize: fontSize.small,
      fontWeight: fontWeight.semiBold,
      lineHeight: 1.4,
      color: colors.oxfordBlue,
      fontFamily: fontFamily.body,
    },

    '& h6': {
      margin: 0,
      fontSize: fontSize.small,
      fontWeight: fontWeight.regular,
      lineHeight: 1.4,
      color: colors.oxfordBlue,
      fontFamily: fontFamily.body,
    },

    '& p': {
      fontFamily: fontFamily.body,
      fontSize: fontSize.default,
      lineHeight: lineHeight.default,
      color: colors.jumbo,
    },

    '& a': {
      fontFamily: fontFamily.body,
      fontSize: fontSize.default,
      lineHeight: 'inherit',
      textDecoration: 'none',
      cursor: 'pointer',

      '&:hover': {
        textDecoration: 'underline',
      },
    },

    '& ul': {
      fontFamily: fontFamily.body,
      fontSize: fontSize.default,
      lineHeight: 1.4,
      color: colors.jumbo,
      margin: 0,
      paddingLeft: spacing.large,

      // When the component is nested in an unordered
      // list the list rendered by the component should
      // behave as if unnested.
      listStyleType: 'disc',

      '& > * + *': {
        marginTop: spacing.small,
      },

      '& li': {
        fontFamily: 'inherit',
        fontSize: 'inherit',
        lineHeight: 'inherit',
      },

      '& ul': {
        listStyleType: 'circle',
      },

      '& ul ul': {
        listStyleType: 'square',
      },
    },

    '& ol': {
      fontFamily: fontFamily.body,
      fontSize: fontSize.default,
      lineHeight: 1.4,
      color: colors.jumbo,
      margin: 0,
      paddingLeft: spacing.large,

      '& > * + *': {
        marginTop: spacing.small,
      },

      '& li': {
        fontFamily: 'inherit',
        fontSize: 'inherit',
        lineHeight: 'inherit',
      },
    },

    '& pre': {
      margin: 0,
      backgroundColor: colors.athensGray,
      border: `1px solid ${colors.catskillWhite}`,
      padding: spacing.default,
      borderRadius: borderRadius.default,
      color: colors.oxfordBlue,
      fontSize: fontSize.small,
      lineHeight: 1.4,
      whiteSpace: 'pre-wrap',
    },

    '& p code': {
      display: 'inline-flex',
      backgroundColor: colors.athensGray,
      border: `1px solid ${colors.catskillWhite}`,
      padding: shorthandHelper([spacing.extraSmall, spacing.small]),
      borderRadius: borderRadius.default,
      color: colors.oxfordBlue,
      fontSize: fontSize.small,
      lineHeight: 1.4,
    },

    '& blockquote': {
      margin: 0,
      paddingLeft: spacing.default,
      color: colors.jumbo,
    },

    '& * + h1': {
      marginTop: spacing.default,
    },

    '& * + h2': {
      marginTop: spacing.default,
    },

    '& * + h3': {
      marginTop: spacing.default,
    },

    '& * + h4': {
      marginTop: spacing.default,
    },

    '& * + h5': {
      marginTop: spacing.default,
    },

    '& * + h6': {
      marginTop: spacing.default,
    },

    '& * + p': {
      marginTop: spacing.default,
    },

    '& p + p': {
      marginTop: spacing.default,
    },

    '& * + ul': {
      marginTop: spacing.default,
    },

    '& * + ol': {
      marginTop: spacing.default,
    },

    '& * + pre': {
      marginTop: spacing.default,
    },

    '& pre + *': {
      marginTop: spacing.default,
    },

    '& p > code + *': {
      marginTop: spacing.default,
    },

    '& * + blockquote': {
      marginTop: spacing.medium,
    },

    '& blockquote + *': {
      marginTop: spacing.medium,
    },

    '& > :first-child': {
      marginTop: 0,
    },

    '& > :last-child': {
      marginBottom: 0,
    },
  }),
}

export default styles

export const previewTheme = theme => {
  const themed = getThemeColors(theme)

  return css({
    '& a': {
      color: themed.base,
    },

    '& blockquote': {
      borderLeft: `3px solid ${colors.geyser}`,
    },
  })
}
